import { ScaleFunction, usePlotArea, useXAxisScale } from 'recharts';
import { Danger600, Warning600, Warning800 } from '../../../../utils/colors';
import { DivergenceRange } from '../divergence-ranges';

interface DivergenceLayerProps {
  ranges: DivergenceRange[];
}

const LABEL_TEXT: Record<DivergenceRange['severity'], string> = {
  note: 'Worth noting (0.5–1%)',
  investigate: 'Needs investigating (>1%)'
};

// A rough per-character width for the 10px label font -- not an exact canvas
// measurement, just enough to keep two labels from landing on top of each other.
const CHAR_WIDTH_PX = 5.5;
const LABEL_GAP_PX = 12;

// A 1-2 week range renders only a few pixels wide against a multi-year x-axis -- easy
// to miss entirely (Sean, 2026-08-29: a "Worth noting" range showed no visible shading
// at all). Widened symmetrically around the range's own midpoint so even a single-week
// range still reads as a real highlighted region, not just a floating label.
const MIN_WIDTH_PX = 10;

// Regular (wide-range) opacity is intentionally faint so a multi-week band doesn't
// overwhelm the chart; narrow (MIN_WIDTH_PX-floored) opacity is intentionally a bit
// stronger since the rect itself is small -- see the isNarrow comment below. Both tiers
// stay in the same faint range as the chart's other background shading (the
// reviewedRange ReferenceArea in DataQaChart.tsx, fillOpacity 0.08) rather than reading
// as a solid block -- a `severity: 'investigate'` range previously hit 0.55 here, opaque
// enough to obscure the underlying series it's meant to call attention to (Sean,
// 2026-08-29).
const REGULAR_OPACITY: Record<DivergenceRange['severity'], number> = {
  note: 0.08,
  investigate: 0.12
};
const NARROW_OPACITY: Record<DivergenceRange['severity'], number> = {
  note: 0.16,
  investigate: 0.22
};

interface MarkerLayout {
  key: string;
  rectX: number;
  rectWidth: number;
  fill: string;
  fillOpacity: number;
  labelX: number;
  textColor: string;
  text: string;
  title: string;
  showLabel: boolean;
}

// Plain data pass (no JSX, no React render involved) so the left-to-right label-cursor
// state lives in a normal local loop rather than being mutated across `.map()`
// iterations inside JSX -- both layers below only ever read this already-computed,
// immutable result, so they can't disagree on layout despite rendering in two different
// places in the tree. Skips a range entirely when its pixel position can't be resolved.
//
// `range.from`/`range.to` are already the chart's own category key (WeekRow.weekLabel,
// e.g. "Jan 2, 2023") -- see divergence-ranges.ts's own doc comment: computeDivergentRanges
// is built from `xValue: w.weekLabel`, NOT weekIso.
const computeMarkerLayout = (ranges: DivergenceRange[], scale: ScaleFunction): MarkerLayout[] => {
  const layout: MarkerLayout[] = [];
  let lastLabelRight = -Infinity;

  ranges.forEach((range, i) => {
    const x1 = scale(range.from, { position: 'start' });
    const x2 = scale(range.to, { position: 'end' });
    if (typeof x1 !== 'number' || typeof x2 !== 'number') return;

    const midX = (x1 + x2) / 2;
    const rawWidth = x2 - x1;
    // A rect this narrow needs more contrast, not just more width, to actually read as
    // a highlight rather than a hairline -- the same 0.12/0.18 opacity tuned for a wide
    // multi-week band (so it doesn't overwhelm the chart across a large area) is too
    // faint once compressed into a MIN_WIDTH_PX-wide sliver.
    const isNarrow = rawWidth < MIN_WIDTH_PX;
    const rectWidth = Math.max(rawWidth, MIN_WIDTH_PX);
    const rectX = midX - rectWidth / 2;

    const investigate = range.severity === 'investigate';
    const opacity = isNarrow ? NARROW_OPACITY[range.severity] : REGULAR_OPACITY[range.severity];
    const text = LABEL_TEXT[range.severity];
    const labelLeft = midX - (text.length * CHAR_WIDTH_PX) / 2;
    const showLabel = labelLeft > lastLabelRight + LABEL_GAP_PX;
    if (showLabel) {
      lastLabelRight = labelLeft + text.length * CHAR_WIDTH_PX;
    }

    layout.push({
      // `i` disambiguates two DIFFERENT series (e.g. two platforms in a per-platform
      // breakout, or two metrics on DependentVarSection's combined chart) that happen
      // to diverge over the exact same (from, to) range -- ranges arriving here are
      // already a flatMap of independently-computed per-series ranges (see
      // ChannelReviewSection/DependentVarSection), so a from-to pair alone is not
      // guaranteed unique. Confirmed live (2026-09-02): React logged a duplicate-key
      // warning once two platforms shared a divergent range.
      key: `${range.from}-${range.to}-${i}`,
      rectX,
      rectWidth,
      fill: investigate ? Danger600 : Warning600,
      fillOpacity: opacity,
      labelX: midX,
      textColor: investigate ? Danger600 : Warning800,
      text,
      title: `${text} — week of ${range.from}${range.to !== range.from ? ` to ${range.to}` : ''}`,
      showLabel
    });
  });
  return layout;
};

// Same Recharts 3.x hook migration as MarginalCaretsLayer (see its own comment) --
// rendered as a direct chart child rather than via the deprecated <Customized> pattern.
// Split into two layers (sharing computeMarkerLayout above) so they can sit on opposite
// sides of the chart's <Area> series in the render tree: the shaded region must be a
// BACKGROUND layer (placed before <Area>) so it never visually competes with the actual
// spend/impressions line at the exact point a reviewer needs to read closely -- a narrow
// range's rect is intentionally quite opaque (see fillOpacity above) precisely because
// it's meant to draw the eye, which would backfire if it sat on top of the data. The
// label, by contrast, must be a FOREGROUND layer (placed after <Area>/MarginalCaretsLayer)
// so it's never itself obscured by the chart fill.
//
// Previously this was one Recharts `<ReferenceArea>` (the shading) plus a small triangle
// marker drawn in pixel space on top -- that split gave the shading no minimum-width
// guarantee (ReferenceArea sizes strictly to its data-domain x1/x2, so a 1-2 week range
// rendered an imperceptibly thin sliver, and the triangle didn't earn its place once the
// shading itself is reliably visible -- Sean, 2026-08-29). Rendering the rect ourselves in
// pixel space (via useXAxisScale/usePlotArea) is what makes the MIN_WIDTH_PX floor
// possible at all.
export const DivergenceRegionsLayer = ({ ranges }: DivergenceLayerProps) => {
  const scale = useXAxisScale();
  const plotArea = usePlotArea();
  if (!scale || !plotArea) return undefined;

  const markers = computeMarkerLayout(ranges, scale);

  return (
    <g className='data-qa-divergence-regions'>
      {markers.map(m => (
        <rect
          key={m.key}
          x={m.rectX}
          y={plotArea.y}
          width={m.rectWidth}
          height={plotArea.height}
          fill={m.fill}
          fillOpacity={m.fillOpacity}
        >
          <title>{m.title}</title>
        </rect>
      ))}
    </g>
  );
};

// See DivergenceRegionsLayer's doc comment above -- this is its foreground half. A label
// that would start before the previous one's right edge (plus a gap) is suppressed
// entirely (see computeMarkerLayout's running cursor) -- the shaded region and its hover
// tooltip still carry the information, just not the always-visible text.
export const DivergenceLabelsLayer = ({ ranges }: DivergenceLayerProps) => {
  const scale = useXAxisScale();
  const plotArea = usePlotArea();
  if (!scale || !plotArea) return undefined;

  const markers = computeMarkerLayout(ranges, scale).filter(m => m.showLabel);

  return (
    <g className='data-qa-divergence-labels'>
      {markers.map(m => (
        <text
          key={m.key}
          x={m.labelX}
          y={plotArea.y + 12}
          textAnchor='middle'
          fontSize={10}
          fill={m.textColor}
        >
          {m.text}
        </text>
      ))}
    </g>
  );
};
